import React, { useState, FormEvent, ChangeEvent } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import './CheckoutForm.css';
import type { CartItem } from '../../types/product';
import type { CheckoutData } from '../../hooks/useCheckout';
import { authFetch } from '../../services/apiAuth';
import { convertCurrency } from '../../utils/currencyUtils';
import { MasterProfile } from '../../types';

// Re-export for backwards compatibility
export type { CartItem, CheckoutData };

// ============================================================================
// Type Definitions
// ============================================================================

/** Supported languages for the checkout form */
export type Language = string;

/** Country codes for shipping */
// TODO: Re-enable additional countries when multi-region shipping is supported
export type CountryCode = 'US' | 'KR' | 'CA';

/** Discount information - kept for reference but CheckoutData uses string */
export interface DiscountInfo {
  code: string;
  percentage: number;
}

/** Shipping address information */
export interface ShippingInfo {
  name: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: CountryCode;
}

/** User object with Firebase auth properties */
export interface User {
  uid: string;
  displayName?: string | null;
  email?: string | null;
}

/** Props for CheckoutFormContent */
export interface CheckoutFormContentProps {
  checkoutData: CheckoutData;
  user?: User | null;
  masterProfile?: MasterProfile | null;
  language?: Language;
  onSuccess: (orderId: string) => void;
  onCancel: () => void;
}

/** Props for the main CheckoutForm component */
export interface CheckoutFormProps extends CheckoutFormContentProps {}

/** Inventory check API response */
interface InventoryCheckResponse {
  allAvailable: boolean;
  outOfStockItems: Array<{
    productId: string;
    requested: number;
    available: number;
  }>;
}

/** Payment intent API response */
interface PaymentIntentResponse {
  clientSecret: string;
  orderId: string;
}

// ============================================================================
// Stripe Initialization
// ============================================================================

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
);

// ============================================================================
// CheckoutFormContent Component
// ============================================================================

/** Whether the current Stripe key is a test-mode key */
const isStripeTestMode = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').startsWith('pk_test_');

const CheckoutFormContent: React.FC<CheckoutFormContentProps> = ({ 
  checkoutData, 
  user, 
  masterProfile,
  language = 'en',
  onSuccess,
  onCancel 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation(['checkout']);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
  }, []);
  // Brings shipping info from user profile if available
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    name: user?.displayName || '',
    email: user?.email || '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    // Force US regardless of profile — US-only shipping for now
    country: 'US' as CountryCode
  });
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Check inventory first
      const inventoryResponse = await authFetch('/api/checkInventory', {
        method: 'POST',
        body: JSON.stringify({ items: checkoutData.items })
      });

      const inventoryResult: InventoryCheckResponse = await inventoryResponse.json();

      if (!inventoryResult.allAvailable) {
        const outOfStockNames = inventoryResult.outOfStockItems
          .map(item => {
            const product = checkoutData.items.find(p => p.id === item.productId);
            return product?.name || item.productId;
          })
          .join(', ');
        
        setError(t('errors.outOfStock', { items: outOfStockNames }));
        setProcessing(false);
        return;
      }

      // Create payment intent on backend
      const response = await authFetch('/api/createPaymentIntent', {
        method: 'POST',
        body: JSON.stringify({
          amount: Math.round(checkoutData.total * 100), // Convert to cents
          currency: 'usd',
          userId: user?.uid,
          items: checkoutData.items,
          discountCode: checkoutData.discount?.code,
          shippingInfo
        })
      });

      const { clientSecret, orderId }: PaymentIntentResponse = await response.json();

      // Get card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError(t('errors.cardNotFound'));
        setProcessing(false);
        return;
      }

      // Confirm card payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: shippingInfo.name,
              email: shippingInfo.email,
              address: {
                line1: shippingInfo.address1,
                line2: shippingInfo.address2,
                city: shippingInfo.city,
                state: shippingInfo.state,
                postal_code: shippingInfo.postalCode,
                country: shippingInfo.country
              }
            }
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Record order in Firebase
        await authFetch('/api/recordOrder', {
          method: 'POST',
          body: JSON.stringify({
            orderId,
            userId: user?.uid,
            paymentIntentId: paymentIntent.id,
            items: checkoutData.items,
            subtotal: checkoutData.subtotal,
            discount: checkoutData.discountAmount,
            total: checkoutData.total,
            shippingInfo,
            discountCode: checkoutData.discount?.code
          })
        });

        onSuccess(orderId);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(t('errors.paymentFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleInputChange = (field: keyof ShippingInfo, value: string): void => {
    setShippingInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="checkout-form">
      <div className="checkout-header">
        <h2>💳 {t('title')}</h2>
        <button onClick={onCancel} className="close-checkout" type="button">×</button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Order Summary */}
        <div className="order-summary">
          <h3>{t('order.summary')}</h3>
          {checkoutData.items.map(item => (
            <div key={item.id} className="summary-item">
              <span>{item.name} × {item.quantity}</span>
              <span>{convertCurrency(item.priceUSD * item.quantity, 'USD').formatted}</span>
            </div>
          ))}
          {checkoutData.discount && checkoutData.discountAmount !== undefined && (
            <div className="summary-item discount">
              <span>{checkoutData.discount.code} ({checkoutData.discount.percentage}%)</span>
              <span>-{convertCurrency(checkoutData.discountAmount, 'USD').formatted}</span>
            </div>
          )}
          <div className="summary-item total">
            <strong>{t('order.total')}</strong>
            <strong>{convertCurrency(checkoutData.total, 'USD').formatted}</strong>
          </div>
        </div>

        {/* Shipping Information */}
        <div className="form-section">
          <h3>{t('shipping.title')}</h3>
          
          <div className="form-row">
            <input
              type="text"
              placeholder={t('shipping.fullName')}
              value={shippingInfo.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
              required
            />
          </div>

          <div className="form-row">
            <input
              type="email"
              placeholder={t('shipping.email')}
              value={shippingInfo.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
              required
            />
          </div>

          <div className="form-row">
            <input
              type="text"
              placeholder={t('shipping.address1')}
              value={shippingInfo.address1}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('address1', e.target.value)}

              pattern={"[A-Za-z0-9\\s.,#\\-'/()]+"}
              title={t('shipping.englishOnly')}
              required
            />
          </div>

          <div className="form-row">
            <input
              type="text"
              placeholder={t('shipping.address2')}
              value={shippingInfo.address2}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('address2', e.target.value)}
              pattern={"[A-Za-z0-9\\s.,#\\-'/()]*"}
              title={t('shipping.englishOnly')}
            />
          </div>

          <div className="form-row-group">
            <input
              type="text"
              placeholder={t('shipping.city')}
              value={shippingInfo.city}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('city', e.target.value)}
              pattern={"[A-Za-z0-9\\s.,#\\-'/()]+"}
              title={t('shipping.englishOnly')}
              required
            />
            <input
              type="text"
              placeholder={t('shipping.state')}
              value={shippingInfo.state}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('state', e.target.value)}
              pattern={"[A-Za-z0-9\\s.,#\\-'/()]+"}
              title={t('shipping.englishOnly')}
              required
            />
          </div>

          <div className="form-row-group">
            <input
              type="text"
              placeholder={t('shipping.zipCode')}
              value={shippingInfo.postalCode}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('postalCode', e.target.value)}
              pattern="[0-9]{5}(-[0-9]{4})?"
              title={t('shipping.zipTitle')}
              required
            />
            {/* Country — soft-disabled to US only for now */}
            {/* TODO: Re-enable multi-country dropdown when international shipping is supported */}
            <select
              value="US"
              disabled
              style={{ backgroundColor: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' }}
            >
              <option value="US">United States</option>
              {/* Future countries (disabled for now):
              <option value="KR">Korea</option>
              <option value="CA">Canada</option>
              */}
            </select>
          </div>
        </div>

        {/* Payment Information */}
        <div className="form-section">
          <h3>{t('payment.title')}</h3>
          {isStripeTestMode && (
            <div className="test-card-banner">
              <span className="test-card-badge">🧪 {t('payment.testMode')}</span>
              <div className="test-card-inline">
                <span className="test-card-item" aria-label={t('payment.cardNumber')}>
                  4242 4242 4242 4242
                  <button
                    type="button"
                    className="copy-btn"
                    aria-label={copied ? t('payment.copied') : t('payment.copyCard')}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText('4242424242424242');
                        setCopied(true);
                        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
                        copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
                      } catch {
                        // Clipboard not available (non-HTTPS or denied)
                      }
                    }}
                  >
                    {copied ? '✓' : '📋'}
                  </button>
                </span>
                <span className="test-card-sep" aria-hidden="true">·</span>
                <span className="test-card-item" aria-label={t('payment.expiry')}>12/30</span>
                <span className="test-card-sep" aria-hidden="true">·</span>
                <span className="test-card-item" aria-label={t('payment.cvc')}>123</span>
                <span className="test-card-sep" aria-hidden="true">·</span>
                <span className="test-card-item" aria-label="ZIP">{t('payment.zipExample')}</span>
              </div>
            </div>
          )}
          <div className="card-element-container">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
        </div>

        {error && <div className="payment-error">{error}</div>}
        <button 
          type="submit" 
          className="pay-button" 
          disabled={!stripe || processing}
        >
          {processing 
            ? t('payment.processing') 
            : t('payment.payAmount', { amount: convertCurrency(checkoutData.total, 'USD').formatted })}
        </button>

        <p className="secure-notice">
          🔒 {t('payment.securePayment')}
        </p>
      </form>
    </div>
  );
};

// ============================================================================
// Main CheckoutForm Component (with Stripe Elements wrapper)
// ============================================================================

const CheckoutForm: React.FC<CheckoutFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutFormContent {...props} />
    </Elements>
  );
};

export default CheckoutForm;
